/**
 * ReceiptTicket - Panneau panier persistant (colonne droite)
 * Style « marché artisanal » : fond crème #fff8ea, header terracotta, titres Domine
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import WeightSelectionModal from '../products/WeightSelectionModal';
import type { CartItem } from '../../../types/ecommerce.types';
import { useEcommerceCart, cleArticle } from '../../../hooks/ecommerce/useEcommerceCart';
import { useTenant } from '../../../contexts/TenantContext';
import { useCustomer } from '../../../contexts/CustomerContext';
import { formatQty } from '../../../utils/pos.utils';
import VidangesAnnonce from '../VidangesAnnonce';
import BacsAnnonce from '../BacsAnnonce';

export default function ReceiptTicket() {
  const navigate = useNavigate();
  const { customer } = useCustomer();
  const { items, getTotals, removeItem, updateQuantity, updateSelection, clearCart, isItemLocked, vidanges, setVidanges, bacs, setBacs } = useEcommerceCart();
  const [editing, setEditing] = useState<CartItem | null>(null);
  const { tenant } = useTenant();
  const totals = getTotals();

  const handleCheckout = () => {
    if (!customer) {
      // Rediriger vers login si pas connecté
      navigate('/shop/login');
      return;
    }
    navigate('/shop/checkout');
  };

  const formatPrice = (cents: number): string => {
    return (cents / 100).toFixed(2);
  };

  const formatDate = (): string => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    };
    const date = now.toLocaleDateString('fr-FR', options);
    const time = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `${date} · ${time}`;
  };

  return (
    <div className="shop-sans flex flex-col h-full bg-[#fff8ea] overflow-hidden">
      {/* Header - Nom boutique + Date */}
      <div className="bg-[#b45f2e] text-white px-5 py-4">
        <h2 className="shop-serif text-sm font-bold uppercase tracking-wide">
          {tenant?.name || 'FOLIES BOUTIK'}
        </h2>
        <p className="text-[11px] opacity-85 mt-0.5">{formatDate()}</p>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {items.length === 0 ? (
          <div className="text-center text-[#6b6550] py-8">
            <div className="text-4xl mb-2">🧺</div>
            <p className="text-base font-semibold text-[#4a4530]">Panier vide</p>
            <p className="text-xs mt-1">Ajoutez des produits</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {items.map((item) => (
              <div
                key={cleArticle(item)}
                className="group relative py-2 border-b border-black/[.06] hover:bg-[#f0e9d8]/50 transition-colors rounded"
              >
                {/* Ligne principale */}
                <div className="flex items-start justify-between gap-2">
                  {/* Quantité + Nom */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1">
                      <span className="font-bold text-[#3a3324] tabular-nums text-[13px]">
                        {formatQty(item.quantity)}×
                      </span>
                      <span className="font-bold text-[#3a3324] leading-tight text-[13px]">
                        {item.product.name}
                      </span>
                      {item.prep_options?.includes('coupé') && (
                        <span className="text-[11px] font-semibold text-[#4a5c3a]">🔪 Coupé</span>
                      )}
                      {item.lot && (
                        <span className="text-[11px] font-semibold text-[#c02626]">
                          🏷️ Anti-gaspi −{item.lot.percent} %{item.lot.expiry_date ? ` · date ${String(item.lot.expiry_date).slice(0, 10).split('-').reverse().slice(0, 2).join('/')}` : ''}
                        </span>
                      )}
                      {/* Afficher le poids choisi pour les produits au poids */}
                      {item.product.is_weighted && item.weight_grams && (
                        <span className="text-[11px] text-[#b45f2e] font-semibold">
                          ({item.weight_grams >= 1000 ? `${formatQty(item.weight_grams / 1000)}kg` : `${formatQty(item.weight_grams)}g`})
                        </span>
                      )}
                    </div>
                    {/* Prix unitaire + Badge préparation */}
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-[11.5px] text-[#6b6550]">
                        {formatPrice(item.unit_price_cents)} €
                        {item.product.is_weighted ? '/kg' : '/unité'}
                      </span>
                      {/* Badge Live Shopping - Statut de préparation */}
                      {item.addedBy && (
                        item.prepared ? (
                          <span className="text-xs bg-[#4a5c3a]/15 text-[#4a5c3a] px-1 py-0.5 rounded-full">
                            ✓
                          </span>
                        ) : (
                          <span className="text-xs bg-[#b45f2e]/15 text-[#b45f2e] px-1 py-0.5 rounded-full">
                            ⏳
                          </span>
                        )
                      )}
                    </div>
                  </div>

                  {/* Prix total */}
                  <div className="font-bold text-[#3a3324] text-[13px] tabular-nums whitespace-nowrap">
                    {formatPrice(item.total_price_cents)} €
                  </div>
                </div>

                {/* Contrôles : à la souris (pointer-fine), ils se superposent à la
                    ligne au survol ; au doigt (iPad, tactile) il n'y a pas de survol,
                    donc ils sont TOUJOURS visibles, sous la ligne, en taille tactile.
                    Avant, sur iPad, le panier s'incrémentait sans qu'on voie jamais
                    les − / + / × (29/08/2026). */}
                {isItemLocked(cleArticle(item)) ? (
                  /* Article verrouillé - icône cadenas */
                  <div className="mt-1.5 ml-auto w-fit pointer-fine:mt-0 pointer-fine:absolute pointer-fine:right-1 pointer-fine:top-1/2 pointer-fine:-translate-y-1/2 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-1 bg-[#f0e9d8] text-[#6b6550] px-1.5 py-0.5 rounded text-xs">
                      <Lock className="w-3 h-3" />
                    </div>
                  </div>
                ) : (
                  /* Article modifiable */
                  <div className="mt-1.5 ml-auto w-fit pointer-fine:mt-0 pointer-fine:absolute pointer-fine:right-1 pointer-fine:top-1/2 pointer-fine:-translate-y-1/2 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 transition-opacity flex items-center gap-0.5 bg-white border border-black/[.15] rounded shadow-sm">
                    <button
                      onClick={() => updateQuantity(cleArticle(item), item.quantity - 1)}
                      className="w-9 h-9 pointer-fine:w-6 pointer-fine:h-6 flex items-center justify-center hover:bg-[#f0e9d8] rounded-l text-[#4a4530] font-bold text-sm"
                      title="Diminuer"
                    >
                      −
                    </button>
                    <span className="w-8 pointer-fine:w-6 text-center font-semibold text-sm pointer-fine:text-xs tabular-nums text-[#3a3324]">
                      {formatQty(item.quantity)}
                    </span>
                    <button
                      onClick={() => updateQuantity(cleArticle(item), item.quantity + 1)}
                      className="w-9 h-9 pointer-fine:w-6 pointer-fine:h-6 flex items-center justify-center hover:bg-[#f0e9d8] text-[#4a4530] font-bold text-sm"
                      title="Augmenter"
                    >
                      +
                    </button>
                    {item.weight_grams ? (
                      <button
                        onClick={() => setEditing(item)}
                        className="w-9 h-9 pointer-fine:w-6 pointer-fine:h-6 flex items-center justify-center hover:bg-[#f0e9d8] text-[#4a4530] text-xs"
                        title="Modifier le poids / le nombre de pièces"
                      >
                        ✎
                      </button>
                    ) : null}
                    <div className="w-px h-5 bg-black/[.15]" />
                    <button
                      onClick={() => removeItem(cleArticle(item))}
                      className="w-9 h-9 pointer-fine:w-6 pointer-fine:h-6 flex items-center justify-center hover:bg-red-50 rounded-r text-red-600 font-bold text-sm"
                      title="Supprimer"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            ))}
            {/* Vidanges rapportées au retrait (crédit FOLIES après recomptage) */}
            <div className="pt-3 space-y-3">
              <VidangesAnnonce value={vidanges} onChange={setVidanges} variante="panier" />
              <BacsAnnonce value={bacs} onChange={setBacs} variante="panier" />
            </div>
          </div>
        )}
      </div>

      {/* Footer - Totaux + Actions */}
      {items.length > 0 && (
        <>
          <div className="border-t border-black/[.10] px-5 py-4 text-[12.5px] text-[#4a4530]">
            {/* Consignes (0 % TVA, remboursées au retour du contenant) */}
            {totals.deposit_lines.map((l) => (
              <div key={l.name} className="flex justify-between items-center mb-1.5">
                <span>♻️ {l.name} × {l.quantity}</span>
                <span className="tabular-nums">{(l.total_cents / 100).toFixed(2)} €</span>
              </div>
            ))}

            {/* Sous-total HT */}
            <div className="flex justify-between items-center mb-1.5">
              <span>Sous-total HT</span>
              <span className="tabular-nums">{totals.subtotal_ht_euros} €</span>
            </div>

            {/* TVA */}
            <div className="flex justify-between items-center">
              <span>TVA</span>
              <span className="tabular-nums">{totals.tax_total_euros} €</span>
            </div>

            {/* Total TTC */}
            <div className="flex justify-between items-center mt-3">
              <span className="shop-serif font-bold text-base text-[#3a3324]">TOTAL TTC</span>
              <span className="shop-serif font-bold text-base text-[#3a3324] tabular-nums">
                {totals.total_ttc_euros} €
              </span>
            </div>
          </div>

          <div className="px-5 pb-5 pt-1 flex flex-col gap-2.5">
            {/* Bouton COMMANDER */}
            <button
              onClick={handleCheckout}
              className="w-full bg-[#b45f2e] hover:bg-[#9d5227] text-white py-[13px] rounded-lg font-bold text-[13.5px] transition-colors active:scale-[0.98]"
            >
              {customer ? 'COMMANDER' : 'CONNEXION'}
            </button>

            {/* Bouton Vider le panier - Masqué si tous les articles sont préparés */}
            {items.some((item) => !isItemLocked(cleArticle(item))) && (
              <button
                onClick={() => {
                  const hasLockedItems = items.some((item) => isItemLocked(cleArticle(item)));
                  const message = hasLockedItems
                    ? 'Les articles préparés par le vendeur seront conservés. Continuer ?'
                    : 'Êtes-vous sûr de vouloir vider votre panier ?';
                  if (confirm(message)) {
                    clearCart();
                  }
                }}
                className="w-full bg-white border border-black/[.15] text-[#4a4530] py-[11px] rounded-lg text-[13px] hover:bg-[#faf7f0] transition-colors"
              >
                Vider le panier
              </button>
            )}
          </div>
        </>
      )}

      {editing && (
        <WeightSelectionModal
          key={cleArticle(editing)}
          isOpen
          onClose={() => setEditing(null)}
          product={editing.product}
          confirmVerb="Modifier"
          initial={{
            weightGrams: editing.weight_grams || 0,
            quantityUnit: editing.quantity_unit || 'grams',
            pieceCount: editing.piece_count,
          }}
          onConfirm={(selection) => updateSelection(cleArticle(editing), selection)}
        />
      )}
    </div>
  );
}
